import _ from 'lodash';
import moment from 'moment';
import supportedLanguages from 'data/languages.json';
import { DEFAULT_LOCALE } from 'config';
import * as CustomValidators from 'components/form/CustomValidators';
import validate from 'validate.js';
import { BOOKING_EVENTS, PAYMENT_EVENTS, MINUTE_FREQUENCY_INTERVALS } from 'data/trident-campaigns';
import { GRABPOINT } from './constants/campaign';
import { ActionFormField } from '../../../types/trident-campaigns';

export const DATE_FORMAT = 'DD/MM/YYYY hh:mm a';
export const SHORT_DATE_FORMAT = 'YYYYMMDD';
export const BOOKING_EVENT_TYPES = ['onBookingComplete', 'onBookingConfirmed', 'onBookingCancelledDriver', 'onBookingCancelledPassenger', 'onBookingDroppingOff', 'onBookingDriverArrived', 'onUnsuccessfulReallocation'];
export const FREQUENCY_TYPES = ['mt', 'da', 'hr', 'wk', 'mi'];
const noSpaceFormat = {
  pattern: /^\S*$/,
  flags: 'i',
  message: "can't contain space.",
};
// overviewConstraints is for targets/OverviewForm
export const overviewConstraints = {
  name: {
    presence: true,
    format: noSpaceFormat,
  },
  status: {
    presence: true,
  },
  countryID: {
    presence: true,
    numericality: { greaterThanOrEqualTo: 1 },
  },
  objective: {
    presence: true,
  },
  targetUserType: {
    presence: true,
  },
  startTime: {
    presence: true,
    dateRange: { start: 'startTime', end: 'endTime', format: DATE_FORMAT },
  },
  endTime: {
    presence: true,
    dateRange: { start: 'startTime', end: 'endTime', format: DATE_FORMAT },
  },
};

// scenarioConstraints is for campaigns/scenarioForm
export function getScenarioConstraints(currentNode: string, scenarioIndex: number) {
  const scenarioConstraints = {
    [`nodes.${currentNode}.data.scenarios.${scenarioIndex}.eventType`]: {
      presence: true,
    },
    [`nodes.${currentNode}.data.meta.startTime`]: {
      presence: true,
      dateRange: { start: `nodes.${currentNode}.data.meta.startTime`, end: `nodes.${currentNode}.data.meta.endTime`, format: DATE_FORMAT },
    },
    [`nodes.${currentNode}.data.meta.endTime`]: {
      presence: true,
      dateRange: { start: `nodes.${currentNode}.data.meta.startTime`, end: `nodes.${currentNode}.data.meta.endTime`, format: DATE_FORMAT },
    },
    [`nodes.${currentNode}.data.scenarios.${scenarioIndex}.taxiTypeIDs`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.scenarios.${scenarioIndex}.group`,
          value: BOOKING_EVENTS,
          in: true,
        }],
        constraints: {
          presence: true,
        },
      },
    },
    [`nodes.${currentNode}.data.scenarios.${scenarioIndex}.pickupCities`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.scenarios.${scenarioIndex}.group`,
          value: BOOKING_EVENTS,
          in: true,
        }],
        constraints: {
          presence: true,
        },
      },
    },
    [`nodes.${currentNode}.data.scenarios.${scenarioIndex}.currency`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.scenarios.${scenarioIndex}.group`,
          value: PAYMENT_EVENTS,
          equal: true,
        }],
        constraints: {
          presence: true,
        },
      },
    },
    limits: {
      validateByFunc: {
        validationFunc: (model: any) => {
          const limits = _.get(model.nodes[currentNode], 'limits', []);

          const int64BitMax = 9223372036854775807;
          const anyLimitBeyond64BitRange = limits.some(element => element.limit >= int64BitMax);
          if (anyLimitBeyond64BitRange) {
            return ' -> scenario limit\'s value must be lesser than 9223372036854775807.';
          }
        },
      },
    },
  };
  return scenarioConstraints;
}

// timeConstraints is for campaigns/timeTriggerForm
export function getTimeConstraints(currentNode: string) {
  const timeConstraints = {
    [`nodes.${currentNode}.data.startTime`]: {
      presence: true,
      dateRange: { start: `nodes.${currentNode}.data.startTime`, end: `nodes.${currentNode}.data.endTime`, format: DATE_FORMAT },
    },
    [`nodes.${currentNode}.data.endTime`]: {
      presence: true,
      dateRange: { start: `nodes.${currentNode}.data.startTime`, end: `nodes.${currentNode}.data.endTime`, format: DATE_FORMAT },
    },
    [`nodes.${currentNode}.data.monthlyFrequency`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.frequency`,
          value: 'mt',
          equal: true,
        }],
        constraints: {
          presence: true,
        },
      },
    },
    [`nodes.${currentNode}.data.interval`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.frequency`,
          value: FREQUENCY_TYPES,
          in: true,
        }],
        constraints: {
          presence: true,
          numericality: {
            onlyInteger: true,
            greaterThan: 0,
            strict: true,
          },
        },
      },
      validateByFunc: {
        validationFunc: (model: any) => {
          const data = _.get(model, `nodes.${currentNode}.data`, {});
          if (data.frequency === 'mi' && !MINUTE_FREQUENCY_INTERVALS.includes(Number(data.interval))) {
            return ` -> interval must be one of ${MINUTE_FREQUENCY_INTERVALS.join(', ')} minutes.`;
          }
          return undefined;
        },
      },
    },
  };
  return timeConstraints;
}

// counter node constraints
export function getCounterConstraints(currentNode: string) {
  const counterConstraints = {
    counter: {
      validateByFunc: {
        validationFunc: (model: any) => {
          if (_.isEmpty(_.get(model.nodes[currentNode], 'data.targets', []))) {
            return 'node should have atleast one count';
          }
          const valuesSoFar = [];
          let hasDuplicate = false;
          _.get(model.nodes[currentNode], 'data.targets').forEach((target) => {
            if (_.includes(valuesSoFar, target)) {
              hasDuplicate = true;
            }
            valuesSoFar.push(target);
          });
          if (hasDuplicate) {
            return 'node can\'t have duplicate counts';
          }
        },
      },
    },
  };
  return counterConstraints;
}

// delay node constraints
export function getDelayConstraints(currentNode: string) {
  const delayConstraints = {
    [`nodes.${currentNode}.data.durationInMin`]: {
      presence: true,
      numericality: {
        onlyInteger: true,
        greaterThan: 0,
        strict: true,
      },
    },
  };
  return delayConstraints;
}

// Experiment node constraints
export function getExperimentConstraints(currentNode: string) {
  const experimentConstraints = {
    [`nodes.${currentNode}.data.groupPercentage`]: {
      validateByFunc: {
        validationFunc: (model: any) => {
          if (_.isEmpty(_.get(model.nodes[currentNode], 'data.groupPercentage', []))) {
            return 'Experiment node should have at least one treatment group';
          }
          const groupPercentage = model.nodes[currentNode].data.groupPercentage;

          if (groupPercentage.some(percentage => percentage === 0)) {
            return 'Treatment group percentage cannot be zero';
          }
          const totalPercentage = _.sum(groupPercentage);
          if (totalPercentage >= 100) {
            return 'Total treatment percentages cannot be equal or more than 100 percent';
          }
        },
      },
    },
  };
  return experimentConstraints;
}

// treatmentActionConstraints is for campaigns/TreatmentActionForm
export const treatmentActionConstraints = {};

// actionAwardRewardConstraints is for campaigns/ActionAwardRewardForm
const getTimedEvent = (nodes, currentNode) => {
  return _.find(nodes, (node) => {
    if (node.type === 'time') {
      // if there is, try to find current node is child of one of those
      const children = _.get(node, 'children', []);
      return children.includes(currentNode);
    }
  });
};

export function getAwardRewardConstraints(currentNode: string, subActionIndex: number) {
  const actionAwardRewardConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.rewardID`]: {
      presence: true,
      numericality: {
        onlyInteger: true,
        greaterThan: 0,
        strict: true,
      },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.amount`]: {
      presence: true,
      numericality: {
        onlyInteger: true,
        greaterThan: 0,
        lessThanOrEqualTo: 50,
        strict: true,
      },
    },
    [`nodes.${currentNode}.data[${subActionIndex}].meta.expireOn`]: {
      validateByFunc: {
        validationFunc: (model: any) => {
          // Skipping other types than expireOn
          const expireType = _.get(model, `nodes.${currentNode}.data.${subActionIndex}.meta.expireType`);
          if (expireType !== 'expireOn') {
            return;
          }
          const expireOn = moment(_.get(model, `nodes.${currentNode}.data[${subActionIndex}].meta.expireOn`), SHORT_DATE_FORMAT, true);
          if (!expireOn.isValid()) {
            return `value is invalid. Must be in this format: ${SHORT_DATE_FORMAT}`;
          }

          const endOfToday = moment().startOf('day');
          if (expireOn.isBefore(endOfToday)) {
            return 'cannot be before today';
          }

          // finding timed event.
          const timeEventNode = getTimedEvent(model.nodes, currentNode);
          if (timeEventNode) {
            const eventStart = timeEventNode.data.startTime ? moment(timeEventNode.data.startTime).endOf('day') : null;
            if (eventStart && expireOn.isBefore(eventStart)) {
              return `cannot be before timed event start date. (${eventStart.format(SHORT_DATE_FORMAT)})`;
            }
          } else {
            const campaignEnd = model.endTime ? moment(model.endTime).startOf('day') : null;
            if (campaignEnd && expireOn.isBefore(campaignEnd)) {
              return `cannot be before campaign end date. (${campaignEnd.format(SHORT_DATE_FORMAT)})`;
            }
          }
        },
      },
    },
  };
  return actionAwardRewardConstraints;
}

// actionAwardSubscriptionConstraints is for targets/ActionAwardSubscriptionForm
export function getActionAwardSubscriptionConstraints(currentNode: string, subActionIndex: number) {
  const actionAwardSubscriptionConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.subscriptionPayload.numBillingCycles`]: {
      presence: true,
      numericality: {
        onlyInteger: true,
        greaterThan: 0,
        strict: true,
      },
    },
  };
  return actionAwardSubscriptionConstraints;
}

export function constructFormConstraints(currentNode: string, subActionIndex: number, formFields: Array<ActionFormField>) {
  const formConstraint = {};
  for (const field of formFields) {
    const { validationRegex, validationMessage, payloadPath } = field;
    formConstraint[`nodes.${currentNode}.data.${subActionIndex}.meta.${payloadPath}`] = {
      presence: _.get(field, 'isRequired', false),
      format: !_.isEmpty(validationRegex) ? {
        pattern: newRegexExpression(validationRegex), // '/^[a-zA-Z0-9]*$/'
        flags: 'i',
        message: validationMessage,
      } : undefined,
    };
  }
  return formConstraint;
}

function newRegexExpression(regexStr: string) {
  // Remove leading and trailing slashes
  const sanitizedStr = regexStr.substring(1, regexStr.length - 1);

  // Create a new RegExp object
  return new RegExp(sanitizedStr);
}

export function getActionAwardNFTConstraints(currentNode: string, subActionIndex: number) {
  const actionAwardNFTConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.awardNFTPayload.web3EventName`]: {
      presence: true,
    },
  };
  return actionAwardNFTConstraints;
}

// actionUpdateQuestConstraints is for campaigns/ActionUpdateQuest
export function getUpdateQuestConstraints(currentNode: string, subActionIndex: number) {
  const actionUpdateQuestConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.updateQuestPayload.challengeID`]: {
      presence: true,
      format: noSpaceFormat,
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.updateQuestPayload.taskID`]: {
      presence: true,
      format: noSpaceFormat,
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.updateQuestPayload.incrementType`]: {
      presence: true,
    },
  };
  return actionUpdateQuestConstraints;
}

// getOptInChallengeConstraints is for campaigns/ActionOptInChallenge
export function getOptInChallengeConstraints(currentNode: string, subActionIndex: number) {
  return {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.optInChallengePayload.challengeID`]: {
      presence: true,
      format: noSpaceFormat,
    },
  };
}

// actionAwardPointConstraints is for campaigns/ActionAwardPointsForm
export function getAwardPointConstraints(currentNode: string, subActionIndex: number) {
  const actionAwardPointConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.dynamicPoint`]: {
      presence: true,
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.purpose`]: {
      presence: true,
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.budgetSource`]: {
      presence: true,
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.gpFunding`]: {
      presence: true,
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo: 0,
        lessThanOrEqualTo: 100,
        strict: true,
      },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.description`]: {
      presence: true,
      ...CustomValidators.constraintTemplates.lengthRange('description', 3, 200),
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.vertical`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.${subActionIndex}.meta.pointCurrency`,
          value: GRABPOINT,
          equal: true,
        }],
        constraints: {
          presence: true,
        },
      },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.product`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.${subActionIndex}.meta.pointCurrency`,
          value: GRABPOINT,
          equal: true,
        }],
        constraints: { presence: true },
      },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.costCenter`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.${subActionIndex}.meta.pointCurrency`,
          value: GRABPOINT,
          equal: true,
        }],
        constraints: { presence: true },
      },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.pricePerPoint`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.${subActionIndex}.meta.pointCurrency`,
          value: GRABPOINT,
          equal: true,
        }],
        constraints: { presence: true },
      },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.financeData.fiatCurrency`]: {
      conditionalConstraints: {
        conditions: [{
          prop: `nodes.${currentNode}.data.${subActionIndex}.meta.pointCurrency`,
          value: GRABPOINT,
          equal: true,
        }],
        constraints: { presence: true },
      },
    },
  };
  return actionAwardPointConstraints;
}

const supportedLanguagesKeys = _.keys(supportedLanguages);

// actionSendGrabchatConstraints is for campaigns/ActionSendGrabChat
export function getActionSendGrabChatConstraints(currentNode: string, subActionIndex: number) {
  const optionalGrabChatConstraints = supportedLanguagesKeys.reduce((result, lang) => {
    if (lang !== DEFAULT_LOCALE) {
      // Require content to be non-empty if corresponding language is selected.
      result[`nodes.${currentNode}.data.${subActionIndex}.meta.grabchatPayload.content.${lang}`] = {
        conditionalConstraints: {
          conditions: [{
            prop: `nodes.${currentNode}.data.${subActionIndex}.meta.grabchatPayload.content.${lang}`,
            value: undefined,
            notEqual: true,
          }],
          constraints: {
            presence: { allowEmpty: false },
          },
        },
      };
    }
    return result;
  }, {});

  const actionSendGrabChatConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.grabchatPayload.content.${DEFAULT_LOCALE}`]: {
      presence: { allowEmpty: false },
    },
    ...optionalGrabChatConstraints,
  };
  return actionSendGrabChatConstraints;
}

// Combine and return the whole constraints
export function getTargetConstraints() {
  return {};
}

// actionSendEmailConstraints is for targets/ActionSendEmailForm

export function getActionSendEmailConstraints(currentNode: string, subActionIndex: number) {
  const SendMailLanguageConstraints = supportedLanguagesKeys.reduce((result, lang) => {
    if (lang !== DEFAULT_LOCALE) {
      result[`nodes.${currentNode}.data.${subActionIndex}.meta.emailPayload.subject.${lang}`] = {
        conditionalConstraints: {
          conditions: [{
            prop: `nodes.${currentNode}.data.${subActionIndex}.meta.emailPayload.subject.${lang}`,
            value: undefined,
            notEqual: true,
          }],
          constraints: {
            presence: {
              presence: { allowEmpty: false },
            },
          },
        },
      };
      result[`nodes.${currentNode}.data.${subActionIndex}.meta.emailPayload.template.${lang}`] = {
        conditionalConstraints: {
          conditions: [{
            prop: `nodes.${currentNode}.data.${subActionIndex}.meta.emailPayload.template.${lang}`,
            value: undefined,
            notEqual: true,
          }],
          constraints: {
            presence: {
              presence: { allowEmpty: false },
            },
          },
        },
      };
    }
    return result;
  }, {});
  const actionSendEmailConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.emailPayload.subject.${DEFAULT_LOCALE}`]: {
      presence: { allowEmpty: false },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.emailPayload.template.${DEFAULT_LOCALE}`]: {
      presence: { allowEmpty: false },
    },
    ...SendMailLanguageConstraints,
  };
  return actionSendEmailConstraints;
}

// ActionUpdateSegmentConstraints is for campaigns/ActionUpdateSegmentForm
export function getActionUpdateSegmentMembershipConstraints(currentNode: string, subActionIndex: number, parentKeyList?: string[]) {
  const actionUpdateSegmentMembershipConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.updateSegMembershipPayload.SegmentName`]: {
      presence: { allowEmpty: false },
    },
    [`nodes.${currentNode}.data.${subActionIndex}.meta.updateSegMembershipPayload.IsRemove`]: {
      presence: true,
    },
    [`nodes.${currentNode}.data[${subActionIndex}].meta.updateSegMembershipPayload.segmentExpiryDate`]: {
      validateByFunc: {
        validationFunc: (model: any) => {
          const isCreateNewSegment = _.get(model, `nodes.${currentNode}.data[${subActionIndex}].meta.updateSegMembershipPayload.isCreateNewSegment`);
          if (!isCreateNewSegment) return undefined;
          const segmentExpiryDate = _.get(model, `nodes.${currentNode}.data[${subActionIndex}].meta.updateSegMembershipPayload.segmentExpiryDate`);
          if (!segmentExpiryDate) return undefined;
          const rootNodeId = parentKeyList && parentKeyList[0];
          let endTime = _.get(model, `nodes[${rootNodeId}].data.meta.endTime`);
          if (!endTime) {
            endTime = _.get(model, 'endTime');
          }
          const expiryMoment = moment(segmentExpiryDate, DATE_FORMAT, true);
          const endTimeMoment = moment(endTime, DATE_FORMAT, true);
          if (expiryMoment.isBefore(endTimeMoment)) {
            return `^Segment expiry date (${expiryMoment.format('DD MMM YYYY HH:mm')}) must be on or after the scenario end time (${endTimeMoment.format('DD MMM YYYY HH:mm')})`;
          }
        },
      },
    },
  };
  return actionUpdateSegmentMembershipConstraints;
}

export function getNewActionSendPushConstraints(currentNode: string, subActionIndex: number) {
  const actionNewSendPushConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.messagingPayload.templateID`]: {
      presence: true,
      validateByFunc: {
        validationFunc: (model: any) => {
          /* error here is an object that contains few fields of error (if any)
          eg: {templateID: 'xxx', buToken: 'xxx'}
          */
          const error = _.get(
            model.nodes[currentNode],
            `data[${subActionIndex}].meta.error`, {}
          );

          const errorString = Object.keys(error || {})
            .map(x => error[x])
            .join('');
          if (errorString) {
            return errorString;
          }
        },
      },
    },
  };

  return actionNewSendPushConstraints;
}

export function getNewActionSendPushLanguageConstraints(currentNode: string, subActionIndex: number, lang: string, fields: any) {
  const newSendPushLanguageConstraints = fields.reduce((result, field) => {
    result[`nodes.${currentNode}.data.${subActionIndex}.meta.messagingPayload.params.${lang}.${field}`] = {
      presence: true,
    };
    return result;
  }, {});

  return newSendPushLanguageConstraints;
}

export function getBusinessUnitTokenConstraints(currentNode: string, subActionIndex: number) {
  const businessUnitTokenConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.messagingPayload.buToken`]: {
      presence: true,
    },
  };
  return businessUnitTokenConstraints;
}

export function getWhatsappTemplateConstraints(currentNode: string, subActionIndex: number, lang: string) {
  const whatsappConstraints = {
    [`nodes.${currentNode}.data.${subActionIndex}.meta.messagingPayload.params.${lang}.twilioStatus`]: {
      validateByFunc: {
        validationFunc: (model: any) => {
          const templateStatus = _.get(model, `nodes.${currentNode}.data.${subActionIndex}.meta.messagingPayload.params.${lang}.twilioStatus`);
          if (templateStatus !== 'approved') {
            return 'Invalid Twilio Template Status';
          }
        },
      },
    },
  };
  return whatsappConstraints;
}

// ActionForm constraints
export function getActionFormConstraints(currentNode: string) {
  const actionFormConstraints = {
    actionNode: {
      validateByFunc: {
        validationFunc: (model: any) => {
          const limits = _.get(model.nodes[currentNode], 'limits', []);
          const limitLength = limits.length;
          if (limitLength > 0 && limitLength < 4) {
            return ' - action limit fields can\'t be empty';
          }
          const int64BitMax = 9223372036854775807;
          const anyLimitBeyond64BitRange = limits.some(element => element.limit >= int64BitMax);
          if (anyLimitBeyond64BitRange) {
            return ' -> action limit\'s value must be lesser than 9223372036854775807.';
          }
          if (_.get(model.nodes[currentNode], 'data', []).length === 0) {
            return ' - atleast one sub-action is required';
          }
          let rewardsValid = true;
          const subActionsList = _.get(model.nodes[currentNode], 'data', []);
          subActionsList.forEach((subAction) => {
            if (subAction.type === 'awardReward' || subAction.type === 'bulkAwardReward') {
              rewardsValid = rewardsValid && subAction.isValid;
            }
          });
          if (!rewardsValid) {
            return ' - not all reward ids are valid in award reward sub-actions.';
          }
        },
      },
    },
  };
  return actionFormConstraints;
}

validate.validators.validateByFunc = CustomValidators.validateByFunc;
validate.validators.hasRules = CustomValidators.hasRules;
